// Análise de imagem no cliente (F-W4): sha256, dimensões e paleta.

export interface Decoded {
  width: number; // dimensões originais
  height: number;
  rgba: ArrayLike<number>; // amostra reduzida a ≤ 64 px no lado maior
}

export async function sha256Hex(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Decodifica e reduz para ≤ 64 px. Lança se o navegador não decodifica o formato (ex.: HEIC fora do Safari). */
export async function decodeImage(file: Blob): Promise<Decoded> {
  const bmp = await createImageBitmap(file);
  const s = Math.min(1, 64 / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * s));
  const h = Math.max(1, Math.round(bmp.height * s));
  const ctx =
    typeof OffscreenCanvas === "function"
      ? new OffscreenCanvas(w, h).getContext("2d")
      : Object.assign(document.createElement("canvas"), { width: w, height: h }).getContext("2d");
  if (!ctx) throw new Error("no_canvas");
  ctx.drawImage(bmp, 0, 0, w, h);
  const out = { width: bmp.width, height: bmp.height, rgba: ctx.getImageData(0, 0, w, h).data };
  bmp.close();
  return out;
}

const hex = (c: number[]) => "#" + c.map((v) => Math.round(v).toString(16).padStart(2, "0")).join("");
const luma = (c: number[]) => 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2];
const dist = (a: number[], b: number[]) => (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2;

/** k-means simples sobre os pixels opacos: até k cores, pesos somam 1, hex minúsculo, maior peso primeiro. */
export function palette(rgba: ArrayLike<number>, k = 5): { palette: Record<string, number>; dominantColor: string } | null {
  const px: number[][] = [];
  for (let i = 0; i + 3 < rgba.length; i += 4) if (rgba[i + 3] >= 128) px.push([rgba[i], rgba[i + 1], rgba[i + 2]]);
  if (!px.length) return null;
  // Sementes por ponto mais distante (determinístico, cobre cores raras); a primeira é a de luminosidade mediana.
  let centers = [[...px].sort((a, b) => luma(a) - luma(b))[px.length >> 1]];
  const near = px.map((p) => dist(p, centers[0]));
  while (centers.length < k) {
    let far = 0;
    for (let i = 1; i < px.length; i++) if (near[i] > near[far]) far = i;
    if (!near[far]) break; // não há mais cores distintas
    centers.push(px[far]);
    px.forEach((p, i) => (near[i] = Math.min(near[i], dist(p, px[far]))));
  }
  let sums: number[][] = [];
  for (let iter = 0; iter < 10; iter++) {
    sums = centers.map(() => [0, 0, 0, 0]);
    for (const p of px) {
      let best = 0;
      let bestD = Infinity;
      centers.forEach((c, j) => {
        const d = dist(p, c);
        if (d < bestD) (bestD = d), (best = j);
      });
      const s = sums[best];
      s[0] += p[0], s[1] += p[1], s[2] += p[2], s[3]++;
    }
    centers = sums.map((s, j) => (s[3] ? [s[0] / s[3], s[1] / s[3], s[2] / s[3]] : centers[j]));
  }
  const weights = new Map<string, number>();
  sums.forEach((s, j) => s[3] && weights.set(hex(centers[j]), (weights.get(hex(centers[j])) ?? 0) + s[3] / px.length));
  const sorted = [...weights].sort((a, b) => b[1] - a[1]);
  return { palette: Object.fromEntries(sorted), dominantColor: sorted[0][0] };
}
